import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import type { DoorPreviewHandle } from '../../components/DoorPreviewGLB.tsx';
import ConfiguratorPreview from '../../components/ConfiguratorPreview';
import AIDoorVisualizer from '../../components/AIDoorVisualizer.tsx';
import FeedbackButton from '../../components/FeedbackButton.tsx';
import { useLanguage } from '../../i18n/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { trackEvent } from '../../analytics';
import { calculatePrice, savePriceSnapshot, loadConfiguratorCatalog } from '../../services/pricingService';
import { optionsForFamily, defaultConfigForFamily } from '../../data/configurator';
import { designSupportsFamily } from '../../data/designFamilies';
import { isSmartline, thresholdForFamily } from '../../data/smartline';
import { glassPatterns, leafGlassSelection, leafGlassSpecification, leafGlassLabel, leafGlassOptionsForFamily, leafGlassSurcharge } from '../../data/leafGlass';
import { lookupColourCode, CUSTOM_COLOUR_SYSTEM } from '../../utils/colourCodes.ts';
import { pageStyle, containerStyle } from './pageStyles';
import { SHOW_CONFIGURATOR_LINKS } from '../../config/features';
import { loadPublishedDesigns } from '../../services/studioCloud';
import { loadPublicVisualSettings, listStudioColours } from '../../services/studioAppearance';
import { COTTAGE_PATTERNS } from '../../geometry/timberLeafSurface';
import { readConfiguratorState, writeConfiguratorState } from './configuratorStorage';

const OPTION_GROUPS = ['colour', 'glass', 'threshold', 'hinge'];
// Configuration types are picked via the door-type × sidelights selector in
// step 2 — setConf() maps the two choices onto the structural type strings
// (SINGLE_DOOR, DOOR_*_SIDELIGHT*, DOUBLE_DOOR, DOUBLE_DOOR_*_SIDELIGHT*).
// Temporarily withheld options (configurator feedback 2026-10-13). Hidden
// options are not offered at all; coming-soon ones are listed but disabled.
const HIDDEN_OPTIONS = new Set(['threshold_accoya']);
const VISIBLE_EXTRAS = new Set(['extra_kickplate', 'extra_waternose']);
const COMING_SOON_GROUPS = new Set(['glass']);
const COMING_SOON_EXEMPT = new Set(['glass_none']);
const isComingSoon = (o: any) => COMING_SOON_GROUPS.has(o.group_key ?? o.group) && !COMING_SOON_EXEMPT.has(o.id);
const offeredOptions = (options: any[]) => options
  .filter((o) => !HIDDEN_OPTIONS.has(o.id))
  .filter((o) => (o.group_key ?? o.group) !== 'extra' || VISIBLE_EXTRAS.has(o.id))
  // A coming-soon option can never be a family default.
  .map((o) => (isComingSoon(o) ? { ...o, comingSoon: true } : o));
const SL_LAYOUTS = ['FULL_GLASS', 'ONE_HORIZONTAL_DIVIDER', 'TWO_HORIZONTAL_DIVIDERS', 'CUSTOM'];
const newSidelight = () => ({ widthMm: 400, heightMm: null as number | null, glassType: 'CLEAR', layout: 'FULL_GLASS' });
const WIZARD_STEPS = [
  { key: 'family', label: 'Door family', labelEt: 'Uksetüüp' },
  { key: 'layout', label: 'Layout & dimensions', labelEt: 'Paigutus ja mõõdud' },
  { key: 'options', label: 'Options', labelEt: 'Valikud' },
  { key: 'summary', label: 'Summary', labelEt: 'Kokkuvõte' }
];

const COLORS = {
  gold: '#dfa926',
  goldSoft: 'rgba(223,169,38,0.12)',
  goldBorder: 'rgba(223,169,38,0.35)',
  surface: 'rgba(28,28,30,0.65)',
  surfaceHover: 'rgba(40,40,43,0.75)',
  textMuted: '#9ca3af',
  textSoft: '#d1d5db',
  text: '#f9fafb',
  border: 'rgba(255,255,255,0.08)',
  borderActive: 'rgba(223,169,38,0.5)',
  radius: '1rem',
  shadow: '0 10px 30px rgba(0,0,0,0.35)'
};

const formatPrice = (v: number) =>
  `€${v.toLocaleString('et-EE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const publicColourLabel = (colour: any) => colour.name && !colour.name.includes(colour.code)
  ? `${colour.name} (${colour.code})` : colour.name || colour.code;

const cardStyle = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: COLORS.radius,
  boxShadow: COLORS.shadow,
  padding: '0.9rem 1rem'
};

const sectionTitleStyle = {
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: COLORS.gold,
  marginBottom: '0.55rem'
};

const labelStyle = {
  display: 'block',
  color: COLORS.textMuted,
  fontSize: '0.74rem',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  marginBottom: '0.25rem'
};

const inputBaseStyle = {
  width: '100%',
  padding: '0.45rem 0.7rem',
  background: 'rgba(17,17,18,0.55)',
  border: `1px solid ${COLORS.border}`,
  borderRadius: '0.6rem',
  color: COLORS.text,
  fontSize: '0.92rem',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  WebkitAppearance: 'none' as const
};

const selectArrow = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.85rem center',
  paddingRight: '2.4rem'
};

// Numeric input that applies every valid keystroke live, without clamping
// mid-typing (e.g. "6" stays as a draft until it becomes "600"). Commits a
// clamped value on blur/Enter.
const LiveNumInput = ({ value, min, max, onCommit, style, ariaLabel }: {
  value: number; min: number; max: number;
  onCommit: (v: number) => void;
  style?: React.CSSProperties; ariaLabel?: string;
}) => {
  const [draft, setDraft] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(String(value)); }, [value]);
  const commit = (raw: string) => {
    const n = Number(raw);
    if (raw !== '' && Number.isFinite(n)) onCommit(Math.round(n));
  };
  return (
    <input
      type="number" step={10} min={min} max={max}
      value={draft}
      aria-label={ariaLabel}
      style={style}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => {
        const s = e.target.value;
        setDraft(s);
        const n = Number(s);
        if (s !== '' && Number.isFinite(n) && n >= min && n <= max) onCommit(Math.round(n));
      }}
      onBlur={() => {
        focused.current = false;
        const n = Math.min(max, Math.max(min, Math.round(Number(draft) || min)));
        setDraft(String(n));
        onCommit(n);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }}
    />
  );
};

const familySectionImg: Record<string, string> = {
  '60_OUT': '/sections/60mmsection.svg',
  '60_OUT_PLUS': '/sections/60mmsection.svg',
  '60_IN': '/sections/60mminwardsection.svg',
  COTTAGE: '/sections/cottagesection.svg',
  MERANTI: '/sections/merantisection.svg',
  '18_DEG': '/sections/18degsection.svg'
};

const Configurator = () => {
  const { t, pick, path, lang } = useLanguage();
  const { addConfiguredItem } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  // Retire the old support link: every configurator must show the real model.
  useEffect(() => {
    if (searchParams.get('preview') !== 'basic') return;
    const next = new URLSearchParams(searchParams);
    next.delete('preview');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [catalog, setCatalog] = useState<any>(null);
  const [initial] = useState(() => readConfiguratorState(searchParams.get('family')));
  const [readyFamilyId, setReadyFamilyId] = useState('');
  const initializedFamily = useRef('');
  const restorePending = useRef(initial.restored);
  const [step, setStep] = useState(initial.step);
  const [familyId, setFamilyId] = useState(initial.familyId);
  const [width, setWidth] = useState(initial.width);
  const [leafW, setLeafW] = useState(initial.leafW);
  const [height, setHeight] = useState(initial.height);
  const [confType, setConfType] = useState(initial.confType);
  const [slSame, setSlSame] = useState(initial.slSame);
  const [slLeft, setSlLeft] = useState<any>(initial.slLeft);
  const [slRight, setSlRight] = useState<any>(initial.slRight);
  const [handing, setHanding] = useState(initial.handing);
  const [opening, setOpening] = useState(initial.opening);
  const [config, setConfig] = useState<any>(initial.config);
  const [manualColourChoice, setManualColourChoice] = useState(false);
  const [publishedDesigns, setPublishedDesigns] = useState<any[]>([]);
  const [visualSettings, setVisualSettings] = useState<any>(null);
  const [publicColours, setPublicColours] = useState<any[]>([]);
  const [studioDesignId, setStudioDesignId] = useState(initial.studioDesignId);
  const [studioError, setStudioError] = useState('');
  const [designLoadError, setDesignLoadError] = useState('');
  const [designReload, setDesignReload] = useState(0);
  useEffect(() => {
    let alive = true;
    const refresh = () => loadPublishedDesigns().then((rows) => {
      if (alive) {
        setPublishedDesigns(rows); setDesignLoadError('');
        setStudioDesignId(current => rows.some(row => row.id === current) ? current : '');
      }
    }).catch(() => { if (alive) setDesignLoadError(lang === 'et' ? 'Disainide laadimine ebaõnnestus.' : 'Could not load door designs.'); });
    const storage = event => { if (event.key === 'hestia.studio.designs-updated') refresh(); };
    const visibility = () => { if (document.visibilityState === 'visible') refresh(); };
    refresh(); window.addEventListener('focus', refresh); window.addEventListener('hestia:designs-updated', refresh);
    window.addEventListener('storage', storage); document.addEventListener('visibilitychange', visibility);
    return () => { alive = false; window.removeEventListener('focus', refresh); window.removeEventListener('hestia:designs-updated', refresh);
      window.removeEventListener('storage', storage); document.removeEventListener('visibilitychange', visibility); };
  }, [lang, designReload]);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      loadPublicVisualSettings().then((settings) => { if (alive) setVisualSettings(settings); }).catch(() => {});
      listStudioColours(true).then((rows) => { if (alive) setPublicColours(rows); }).catch(() => {});
    };
    const storageChanged = (event: StorageEvent) => { if (event.key === 'hestia.studio.appearance-updated') refresh(); };
    refresh(); window.addEventListener('focus', refresh); window.addEventListener('hestia:appearance-updated', refresh); window.addEventListener('storage', storageChanged);
    return () => { alive = false; window.removeEventListener('focus', refresh); window.removeEventListener('hestia:appearance-updated', refresh); window.removeEventListener('storage', storageChanged); };
  }, []);
  const [extras, setExtras] = useState<string[]>(initial.extras);
  const [qty, setQty] = useState(initial.qty);
  const [openAngle, setOpenAngle] = useState(initial.openAngle);
  const [passiveAngle, setPassiveAngle] = useState(initial.passiveAngle);
  const [price, setPrice] = useState<any>(null);
  const [added, setAdded] = useState(false);
  const [vizOpen, setVizOpen] = useState(false);
  const previewRef = useRef<DoorPreviewHandle>(null);

  useEffect(() => {
    loadConfiguratorCatalog().then(setCatalog);
  }, []);

  // Persist the picked family so a reload keeps it (URL param already wins on init).
  useEffect(() => {
    try { localStorage.setItem('cfg.family', familyId); } catch { /* storage unavailable */ }
  }, [familyId]);

  // If a cached family no longer exists in the catalog, fall back to the first.
  useEffect(() => {
    if (catalog?.families?.length && !catalog.families.some((f: any) => f.id === familyId)) {
      setFamilyId(catalog.families[0].id);
    }
  }, [catalog, familyId]);

  const family = useMemo(
    () => catalog?.families.find((f: any) => f.id === familyId),
    [catalog, familyId]
  );
  const leafGlassOptions = leafGlassOptionsForFamily(familyId);
  const fixedThreshold = isSmartline(familyId);
  const threshold = thresholdForFamily(familyId, config.threshold);
  const options = useMemo(() => offeredOptions(catalog?.options || []), [catalog]);
  const grouped = useMemo(
    () => (family ? optionsForFamily(family.id, options, catalog.compatibility) : {}),
    [catalog, family, options]
  );

  useEffect(() => {
    if (!family || initializedFamily.current === family.id) return;
    initializedFamily.current = family.id;
    const restore = restorePending.current && initial.familyId === family.id;
    restorePending.current = false;
    const defaults = defaultConfigForFamily(family.id, options.filter((o: any) => !o.comingSoon), catalog.compatibility);
    const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    if (restore) {
      const restored: any = { ...defaults };
      Object.entries(initial.config).forEach(([key, value]) => {
        if (key === 'glass' && ['glass_8mm_clear', 'glass_8mm_frosted'].includes(value)) {
          const migrated = leafGlassSelection(value, initial.config.glassPattern, family.id);
          restored.glass = migrated.option.id;
          if (migrated.pattern) restored.glassPattern = migrated.pattern.id;
          return;
        }
        if (key === 'colourCode' || (key === 'cottagePattern' && ['vertical', 'chevron'].includes(value))
          || (key === 'glassPattern' && glassPatterns.some(p => p.id === value))
          || (key === 'glass' && leafGlassOptions.some(o => o.id === value))
          || grouped[key]?.some((o: any) => o.id === value && !o.comingSoon)) restored[key] = value;
      });
      const double = initial.confType.startsWith('DOUBLE_DOOR') && grouped.design?.some((o: any) => o.id === 'design_double');
      const type = !double && initial.confType.startsWith('DOUBLE_DOOR')
        ? (initial.confType === 'DOUBLE_DOOR' ? 'SINGLE_DOOR' : initial.confType.replace('DOUBLE_DOOR_', 'DOOR_')) : initial.confType;
      restored.sidelight = type.includes('TWO_SIDELIGHTS') ? 'sidelight_both' : type.includes('LEFT') ? 'sidelight_left' : type.includes('RIGHT') ? 'sidelight_right' : 'sidelight_none';
      restored.threshold = thresholdForFamily(family.id, restored.threshold);
      setConfig(restored);
      setConfType(type);
      setWidth(double ? initial.leafW.l + initial.leafW.r : clamp(initial.width, family.minWidth, family.maxWidth));
      setHeight(clamp(initial.height, family.minHeight, family.maxHeight));
      setExtras(initial.extras.filter(id => grouped.extra?.some((o: any) => o.id === id && !o.comingSoon)));
      setOpening(family.opening === 'both' ? initial.opening : family.opening);
      setReadyFamilyId(family.id);
      return;
    }
    setConfig({ ...defaults, threshold: thresholdForFamily(family.id, defaults.threshold) });
    // Extras flagged is_default in compatibility (e.g. waternose on 40 mm
    // families) start pre-selected; others start empty.
    setExtras((optionsForFamily(family.id, options, catalog.compatibility).extra || [])
      .filter((o: any) => o.isDefault).map((o: any) => o.id));
    // Keep the visitor's dimensions across family switches — only clamp them
    // into the new family's range (resetting to its reference size made the
    // door visibly jump, e.g. 2100 → 2000 mm).
    setWidth((w) => clamp(w, family.minWidth, family.maxWidth));
    setHeight((h) => clamp(h, family.minHeight, family.maxHeight));
    setConfType('SINGLE_DOOR');
    setSlSame(true);
    setSlLeft(newSidelight());
    setSlRight(newSidelight());
    setOpening(family.opening === 'both' ? 'outward' : family.opening);
    setAdded(false);
    setReadyFamilyId(family.id);
  }, [family, catalog, options, grouped, initial, leafGlassOptions]);

  useEffect(() => {
    // Wait until family defaults/restoration finish, including after a family switch.
    if (readyFamilyId !== familyId) return;
    writeConfiguratorState({ familyId, step, width, leafW, height, confType, slSame, slLeft, slRight,
      handing, opening, config, studioDesignId, extras, qty, openAngle, passiveAngle });
  }, [readyFamilyId, familyId, step, width, leafW, height, confType, slSame, slLeft, slRight,
    handing, opening, config, studioDesignId, extras, qty, openAngle, passiveAngle]);

  // Every available door family supports sidelights.
  const slSupported = !!family;
  const isDouble = confType.startsWith('DOUBLE_DOOR');
  const hasLeft = confType === 'DOOR_LEFT_SIDELIGHT' || confType === 'DOOR_TWO_SIDELIGHTS'
    || confType === 'DOUBLE_DOOR_LEFT_SIDELIGHT' || confType === 'DOUBLE_DOOR_TWO_SIDELIGHTS';
  const hasRight = confType === 'DOOR_RIGHT_SIDELIGHT' || confType === 'DOOR_TWO_SIDELIGHTS'
    || confType === 'DOUBLE_DOOR_RIGHT_SIDELIGHT' || confType === 'DOUBLE_DOOR_TWO_SIDELIGHTS';
  const effSlRight = slSame ? slLeft : slRight;
  const doubleAllowed = !!grouped.design?.some((o: any) => o.id === 'design_double');
  const availableDesigns = publishedDesigns.filter(row => designSupportsFamily(row, familyId));
  useEffect(() => {
    if (publishedDesigns.some(row => row.id === studioDesignId && !designSupportsFamily(row, familyId))) setStudioDesignId('');
  }, [publishedDesigns, studioDesignId, familyId]);
  const studioDesign = availableDesigns.find((row) => row.id === studioDesignId)?.design;
  const hasLeafOpenings = !!studioDesign?.openings?.length;
  const doorDesign = isDouble ? 'design_double' : hasLeafOpenings ? 'design_glass' : 'design_blind';
  const selectedGlass = leafGlassSelection(config.glass, config.glassPattern, familyId);
  const leafGlass = hasLeafOpenings ? selectedGlass.option.id : 'glass_none';
  const leafGlassPattern = hasLeafOpenings ? selectedGlass.pattern?.id || null : null;
  const glassSurcharge = hasLeafOpenings ? leafGlassSurcharge(familyId, leafGlass) : null;
  const glassLabel = leafGlassLabel(leafGlass, leafGlassPattern, lang) || '';
  const cottagePattern = config.cottagePattern || studioDesign?.cottagePattern || 'chevron';
  const leafGeometry = useMemo(() => family?.id === 'COTTAGE'
    ? { ...studioDesign, cottagePattern } : studioDesign || undefined, [family?.id, studioDesign, cottagePattern, threshold]);
  const panelLabel = lang === 'et' ? 'Paneelimuster' : 'Panel pattern';
  const panelName = cottagePattern === 'vertical' ? (lang === 'et' ? 'Püstised paneelid' : 'Vertical panels')
    : (lang === 'et' ? 'Kuusemuster' : 'Chevron panels');

  const sidePayload = (s: any) => ({
    width_mm: s.widthMm,
    ...(s.heightMm ? { height_mm: s.heightMm } : {}),
    glass_type: s.glassType,
    layout: s.layout
  });

  const applyConfType = (ct: string) => {
    setConfType(ct);
    setAdded(false);
    const wasDouble = confType.startsWith('DOUBLE_DOOR');
    if (ct.startsWith('DOUBLE_DOOR')) {
      // Single → double: both leaves get the current (full) leaf width instead
      // of splitting it into two narrow leaves. Double ↔ double keeps them.
      if (!wasDouble) {
        const leaf = Math.min(1500, Math.max(300, width));
        setLeafW({ l: leaf, r: leaf });
        setWidth(leaf * 2);
      }
    } else if (wasDouble) {
      // Double → single: the door returns to one leaf's width.
      setWidth(Math.min(family.maxWidth, Math.max(family.minWidth, leafW.l)));
    } else if (width > family.maxWidth || width < family.minWidth) {
      setWidth(Math.min(family.maxWidth, Math.max(family.minWidth, width)));
    }
    setConfig((c: any) => {
      const next = { ...c };
      next.sidelight = ct === 'SINGLE_DOOR' || ct === 'DOUBLE_DOOR' ? 'sidelight_none'
        : ct.includes('LEFT') ? 'sidelight_left'
        : ct.includes('RIGHT') ? 'sidelight_right'
        : 'sidelight_both';
      return next;
    });
  };

  const setLeafSide = (side: 'l' | 'r', v: number) => {
    const clamped = Math.min(1500, Math.max(300, Math.round(v || 0)));
    const next = { ...leafW, [side]: clamped };
    setLeafW(next);
    setWidth(next.l + next.r);
    setAdded(false);
  };

  const setSl = (side: 'left' | 'right') => (patch: any) => {
    setAdded(false);
    if (side === 'left') {
      setSlLeft((s: any) => {
        const next = { ...s, ...patch };
        if (slSame) setSlRight(next);
        return next;
      });
    } else {
      setSlRight((s: any) => ({ ...s, ...patch }));
    }
  };

  useEffect(() => {
    if (!family || readyFamilyId !== family.id) return;
    let active = true;
    const input = {
      family: family.id, qty, width_mm: width, height_mm: height,
      design: doorDesign, colour: config.colour, glass: leafGlass, glassPattern: leafGlassPattern,
      ...(family.id === 'COTTAGE' ? { cottagePattern } : {}),
      frame: config.frame, threshold, lock: config.lock,
      hinge: config.hinge,
      hinges: config.hinge === 'hinge_2' ? 2 : 3,
      sidelight: config.sidelight,
      configuration_type: confType,
      left_sidelight: hasLeft ? sidePayload(slLeft) : null,
      right_sidelight: hasRight ? sidePayload(effSlRight) : null,
      sidelight_same: slSame,
      extras, opening, handing
    };
    const timer = setTimeout(() => {
      calculatePrice(input).then((result: any) => {
        if (active) setPrice(!studioDesign ? result : {
          ...result,
          // The new opening/cassette pattern has no catalogue price yet.
          retailPrice: null, totalPrice: null, requiresReview: true, confidence: 'unpriced'
        });
      });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [family, readyFamilyId, width, height, confType, slLeft, slRight, slSame, hasLeft, hasRight, effSlRight, config, extras, opening, handing, qty, doorDesign, leafGlass, leafGlassPattern, studioDesign, cottagePattern, threshold]);

  // Custom NCS / RAL colour — the entered code is looked up on every keystroke
  // and drives the swatch + 3D tone; an empty code is "to be confirmed".
  const customSystem = CUSTOM_COLOUR_SYSTEM[config.colour];
  const colourCodeEntered = !!customSystem && !!(config.colourCode || '').trim();
  const colourMatch = customSystem ? lookupColourCode(customSystem, config.colourCode || '') : null;
  // Published colours are normal customer choices. Keep the pricing option
  // and canonical code underneath so existing drafts and orders still work.
  const listedColours = useMemo(() => {
    const unique = new Map();
    publicColours.forEach((row) => {
      if (!row.published || !['RAL', 'NCS'].includes(row.system)) return;
      const optionId = `colour_custom_${row.system.toLowerCase()}`;
      if (!grouped.colour?.some((option: any) => option.id === optionId && !option.comingSoon)) return;
      const match = lookupColourCode(row.system, row.code);
      if (!match) return;
      const key = `${row.system}:${match.code}`;
      if (!unique.has(key)) unique.set(key, { ...row, code: match.code, optionId });
    });
    return [...unique.values()];
  }, [publicColours, grouped.colour]);
  const selectedPublicColour = !manualColourChoice && listedColours.find((row) =>
    row.optionId === config.colour && row.code === colourMatch?.code);

  const setOpt = (group: string, id: string) => {
    if (group === 'colour') setManualColourChoice(!!CUSTOM_COLOUR_SYSTEM[id]);
    // A different colour option starts with an empty custom code.
    setConfig((c: any) => ({ ...c, [group]: id, ...(group === 'colour' && id !== c.colour ? { colourCode: '' } : {}) }));
    setAdded(false);
  };

  const toggleExtra = (id: string) => {
    setExtras((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
    setAdded(false);
  };

  const addToCart = async () => {
    if (studioError) return;
    const item = {
      family: family.id,
      variant: doorDesign,
      dimensions: { width_mm: width, height_mm: height },
      design: doorDesign,
      colour: config.colour,
      colourCode: customSystem ? (colourMatch?.code || (config.colourCode || '').trim() || null) : null,
      colourHex: colourMatch?.hex ?? null,
      glass: leafGlass,
      glassPattern: leafGlassPattern,
      glassSpecification: leafGlassSpecification(leafGlass, leafGlassPattern),
      glassSurcharge,
      studioDesignId: studioDesign ? studioDesignId : null,
      leafGeometry: leafGeometry || null,
      ...(family.id === 'COTTAGE' ? { cottagePattern } : {}),
      frame: config.frame,
      threshold,
      lock: config.lock,
      sidelight: config.sidelight,
      configurationType: confType,
      leftLeafWidthMm: isDouble ? leafW.l : null,
      rightLeafWidthMm: isDouble ? leafW.r : null,
      leftSidelight: hasLeft ? sidePayload(slLeft) : null,
      rightSidelight: hasRight ? sidePayload(effSlRight) : null,
      sidelightSame: slSame,
      extras,
      opening,
      handing,
      unitPrice: price?.retailPrice ?? null,
      quantity: qty,
      currency: price?.currency ?? 'EUR',
      confidence: price?.confidence ?? 'unpriced',
      requiresReview: price?.requiresReview ?? true,
      priceList: price?.priceList ?? null
    };
    addConfiguredItem(item, qty);
    savePriceSnapshot({ family: family.id, qty, width_mm: width, height_mm: height, configuration_type: confType, left_leaf_width_mm: isDouble ? leafW.l : null, right_leaf_width_mm: isDouble ? leafW.r : null, left_sidelight: hasLeft ? sidePayload(slLeft) : null, right_sidelight: hasRight ? sidePayload(effSlRight) : null, sidelight_same: slSame, ...config, design: doorDesign, glass: leafGlass, glassPattern: leafGlassPattern, glassSpecification: item.glassSpecification, glassSurcharge, threshold, ...(family.id === 'COTTAGE' ? { cottagePattern } : {}), extras, opening, handing }, price);
    trackEvent('add_to_cart', { product: family.id, configuration: 'configured', requires_review: item.requiresReview });
    setAdded(true);
  };

  const optionLabel = (id: string) => {
    const opt = leafGlassOptions.find((o) => o.id === id) || (catalog?.options || []).find((o: any) => o.id === id);
    return opt ? pick(opt, 'name') : id;
  };

  const renderSelect = (group: string) => {
    if (group === 'threshold' && fixedThreshold) return null;
    const opts = group === 'glass' ? (hasLeafOpenings ? leafGlassOptions : []) : grouped[group];
    if (!opts || opts.length === 0) return null;
    const displayedOptions = group === 'colour' ? [
      ...opts.filter((option: any) => !CUSTOM_COLOUR_SYSTEM[option.id]),
      ...listedColours.map((row) => ({ id: `public:${row.id}`, name: publicColourLabel(row), nameEt: publicColourLabel(row) })),
      ...opts.filter((option: any) => CUSTOM_COLOUR_SYSTEM[option.id])
    ] : opts;
    return (
      <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        <label style={labelStyle} htmlFor={`cfg-${group}`}>{t(`configurator.groups.${group}`)}</label>
        <select
          id={`cfg-${group}`}
          value={group === 'glass' ? leafGlass : group === 'colour' && selectedPublicColour ? `public:${selectedPublicColour.id}` : config[group] || ''}
          onChange={(e) => {
            const colour = group === 'colour' && listedColours.find((row) => `public:${row.id}` === e.target.value);
            if (colour) {
              setManualColourChoice(false);
              setConfig((current: any) => ({ ...current, colour: colour.optionId, colourCode: colour.code }));
              setAdded(false);
            } else setOpt(group, e.target.value);
          }}
          style={{ ...inputBaseStyle, ...selectArrow }}
        >
          {displayedOptions.map((o: any) => (
            <option key={o.id} value={o.id} disabled={o.comingSoon}>
              {pick(o, 'name')}{group === 'glass' && o.surcharge != null ? (o.surcharge === 0 ? (lang === 'et' ? ' · hinnas' : ' · included') : ` · +${formatPrice(o.surcharge)}`) : ''}{o.comingSoon ? ` — ${t('configurator.comingSoon')}` : ''}
            </option>
          ))}
        </select>
        {group === 'glass' && glassSurcharge != null && <p style={{ color: COLORS.textMuted, fontSize: '0.85rem', margin: 0 }}>
          {lang === 'et' ? 'Klaasi lisatasu on ühe ukse kohta, sõltumata klaasiavade arvust.' : 'Glass surcharge is per door, regardless of the number of glass openings.'}
        </p>}
        {group === 'glass' && selectedGlass.option.patterned && <>
          <label style={labelStyle} htmlFor="cfg-glass-pattern">{lang === 'et' ? 'Klaasi muster' : 'Glass pattern'}</label>
          <select id="cfg-glass-pattern" value={leafGlassPattern || ''} style={{ ...inputBaseStyle, ...selectArrow }}
            onChange={(e) => { setConfig((current: any) => ({ ...current, glassPattern: e.target.value })); setAdded(false); }}>
            {glassPatterns.map(pattern => <option key={pattern.id} value={pattern.id}>{pattern.name}</option>)}
          </select>
          <p style={{ color: COLORS.textMuted, fontSize: '0.85rem', margin: 0 }}>
            {lang === 'et' ? 'Lamineeritud musterklaas on saadaval 3K paketina.' : 'Laminated patterned glass is available as a 3K unit.'}
          </p>
        </>}
        {group === 'colour' && customSystem && !selectedPublicColour && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center' }}>
              <span
                aria-hidden
                style={{
                  flex: '0 0 auto', width: '2rem', height: '2rem', borderRadius: '0.45rem',
                  border: `1px solid ${COLORS.border}`,
                  background: colourMatch ? colourMatch.hex
                    : 'repeating-linear-gradient(45deg, #2a2a2d 0 6px, #1c1c1e 6px 12px)'
                }}
              />
              <input
                type="text"
                aria-label={t(`configurator.colourCode.label${customSystem}`)}
                placeholder={t(`configurator.colourCode.placeholder${customSystem}`)}
                value={config.colourCode || ''}
                maxLength={40}
                onChange={(e) => setOpt('colourCode', e.target.value)}
                style={inputBaseStyle}
              />
            </div>
            <span style={{ fontSize: '0.74rem', lineHeight: 1.4, color: colourCodeEntered && !colourMatch ? '#f59e0b' : COLORS.textMuted }}>
              {!colourCodeEntered ? t('configurator.colourCode.optional')
                : colourMatch ? `${colourMatch.code}${colourMatch.name ? ` · ${colourMatch.name}` : ''} — ${t('configurator.colourCode.indicative')}`
                : t(`configurator.colourCode.notFound${customSystem}`)}
            </span>
          </div>
        )}
      </div>
    );
  };

  const familySubtitle = (f: any) => {
    const dims = `${f.minWidth}–${f.maxWidth} × ${f.minHeight}–${f.maxHeight} mm`;
    const openingText = f.opening === 'both'
      ? `${t('configurator.outward')} / ${t('configurator.inward')}`
      : t(`configurator.${f.opening}`);
    return `${dims} · ${openingText}`;
  };

  return (
    <div style={pageStyle}>
      <style>{`
        .cfg-grid {
          --cfg-left: 44rem;
          display: grid;
          grid-template-columns: var(--cfg-left) 1fr;
          gap: 1.5rem;
          align-items: start;
        }
        .cfg-preview-col {
          position: fixed;
          top: 4rem;
          right: 0;
          bottom: 0;
          width: calc(100vw - var(--cfg-left) - 3rem);
          display: flex;
          flex-direction: column;
        }
        .cfg-preview-card {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
        }
        .cfg-preview-canvas {
          flex: 1;
          min-height: 0;
          position: relative;
        }
        .cfg-intro {
          width: 44rem;
          max-width: calc(100vw - 3rem);
          text-align: left;
        }
        .cfg-controls {
          min-width: 0;
        }
        .cfg-family-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.65rem;
        }
        .cfg-footer {
          display: none;
        }
        .cfg-feedback-btn {
          position: fixed;
          left: 1rem;
          bottom: 1rem;
          z-index: 40;
        }
        .cfg-nav-buttons {
          margin-bottom: 2.5rem;
        }
        .cfg-family-card:focus-visible,
        .cfg-step-button:focus-visible {
          outline: 2px solid ${COLORS.gold};
          outline-offset: 2px;
        }
        @media (max-width: 1500px) and (min-width: 1101px) {
          .cfg-grid {
            --cfg-left: 32rem;
          }
          .cfg-intro {
            width: 32rem;
          }
          .cfg-family-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 1100px) and (min-width: 901px) {
          .cfg-grid {
            --cfg-left: 22rem;
          }
          .cfg-intro {
            width: 22rem;
          }
          .cfg-family-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        /* Mobile: app-style screen under the 4rem nav — the 3D door fills the
           top, controls live in a compact scrollable panel at the bottom. */
        @media (max-width: 900px) {
          .cfg-grid {
            position: fixed;
            top: 4rem;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0;
            z-index: 10;
            background: #0d0d0e;
          }
          .cfg-intro {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            white-space: nowrap;
          }
          .cfg-preview-col {
            position: relative;
            order: -1;
            top: auto;
            width: auto;
            flex: 1 1 auto;
            min-height: 14rem;
          }
          .cfg-controls {
            flex: 0 1 auto;
            max-height: 48dvh;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            padding: 0.6rem 0.75rem 0;
            gap: 0.6rem !important;
            border-top: 1px solid ${COLORS.border};
          }
          .cfg-family-grid {
            grid-auto-flow: column;
            grid-template-columns: none;
            grid-auto-columns: 42%;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            padding-bottom: 0.2rem;
          }
          .cfg-family-card {
            min-height: 0 !important;
            scroll-snap-align: start;
          }
          .cfg-family-card img {
            height: 3rem !important;
          }
          .cfg-nav-buttons {
            position: sticky;
            bottom: 0;
            padding: 0.6rem 0 0.75rem !important;
            background: #0d0d0e;
            z-index: 1;
          }
          .cfg-footer {
            display: none;
          }
          .cfg-nav-buttons {
            margin-bottom: 0;
          }
          /* 3D view gets the bulk of the screen; controls panel is capped. */
          .cfg-preview-col {
            flex: 1 1 60%;
          }
          .cfg-controls {
            max-height: 40dvh !important;
          }
          /* Compact step tabs: labels only, one slim row. */
          .cfg-steps {
            padding: 0.25rem 0.35rem !important;
            border-radius: 0.6rem !important;
          }
          .cfg-step-num {
            display: none !important;
          }
          .cfg-step-button {
            padding: 0.35rem 0.1rem !important;
            font-size: 0.66rem !important;
          }
          /* Slim one-line price bar. */
          .cfg-price-bar {
            padding: 0.35rem 0.75rem !important;
          }
          .cfg-price-label {
            display: none !important;
          }
          .cfg-price-value {
            font-size: 1.1rem !important;
          }
          .cfg-price-note {
            font-size: 0.62rem !important;
            line-height: 1.2;
          }
          /* AI preview button: top-right under the size pill, out of the door's way. */
          .cfg-ai-btn {
            top: 2.6rem !important;
            right: 0.75rem !important;
            bottom: auto !important;
            left: auto !important;
            padding: 0.3rem 0.65rem !important;
            font-size: 0.68rem !important;
          }
          /* bottom of the screen belongs to the controls panel on mobile */
          .cfg-feedback-btn {
            top: calc(4rem + 0.75rem);
            bottom: auto;
            left: 0.75rem;
          }
        }
      `}</style>
      <SEO
        title={`${t('configurator.title')} | Hestia Doors`}
        description={t('configurator.metaDesc')}
        canonicalPath="/configurator"
        noindex={!SHOW_CONFIGURATOR_LINKS}
      />
      <Navigation />

      <div style={{ ...containerStyle, maxWidth: '100%', paddingTop: '1.5rem', paddingBottom: '0', paddingRight: 0 }}>
        <div className="cfg-intro" style={{ marginBottom: '1.25rem', padding: '0 0.25rem' }}>
          <h1 style={{ fontSize: 'clamp(1.7rem, 3vw, 2rem)', lineHeight: 1.1, fontWeight: 800, color: COLORS.text, margin: '0 0 0.55rem', letterSpacing: '-0.025em' }}>
            {t('configurator.title')}
          </h1>
          <p style={{ color: COLORS.textMuted, fontSize: '0.9rem', lineHeight: 1.55, margin: 0 }}>
            {t('configurator.intro')}
          </p>
        </div>

        {!catalog || !family || readyFamilyId !== family.id ? (
          <p style={{ color: COLORS.textMuted }}>{t('configurator.loading')}</p>
        ) : (
          <div className="cfg-grid">
            {/* LEFT COLUMN — controls */}
            <div className="cfg-controls" style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div className="cfg-steps" style={{ ...cardStyle, padding: '0.8rem', background: 'rgba(24,24,26,0.88)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${WIZARD_STEPS.length}, 1fr)`, gap: '0.3rem' }}>
                  {WIZARD_STEPS.map((item, index) => {
                    const number = index + 1;
                    const active = number === step;
                    const complete = number < step;
                    return (
                      <button
                        key={item.key}
                        className="cfg-step-button"
                        type="button"
                        onClick={() => setStep(number)}
                        style={{
                          border: 0, padding: '0.4rem 0.1rem 0.45rem', cursor: 'pointer', minWidth: 0,
                          background: 'transparent', color: active || complete ? COLORS.gold : COLORS.textMuted,
                          fontSize: '0.68rem', lineHeight: 1.25, fontWeight: active ? 700 : 500,
                          borderBottom: `2px solid ${active ? COLORS.gold : complete ? COLORS.goldBorder : COLORS.border}`
                        }}
                      >
                        <span className="cfg-step-num" style={{ display: 'block', fontSize: '0.72rem', marginBottom: '0.15rem' }}>{number}</span>
                        {lang === 'et' ? item.labelEt : item.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Family selector */}
              {step === 1 && <div style={{ ...cardStyle, padding: '1rem' }}>
                <h2 style={{ ...sectionTitleStyle, marginBottom: '0.75rem' }}>{t('configurator.familyLabel')}</h2>
                <div className="cfg-family-grid">
                  {catalog.families.map((f: any) => {
                    const active = f.id === familyId;
                    return (
                      <button
                        key={f.id}
                        className="cfg-family-card"
                        onClick={() => {
                          setFamilyId(f.id);
                          // A product link may preselect a family. Keep that URL in
                          // sync so refresh cannot override a later manual choice.
                          if (searchParams.has('family')) {
                            const next = new URLSearchParams(searchParams);
                            next.set('family', f.id); setSearchParams(next, { replace: true });
                          }
                        }}
                        style={{
                          width: '100%',
                          minHeight: '10.5rem',
                          textAlign: 'center',
                          padding: '0.7rem 0.65rem',
                          borderRadius: '0.7rem',
                          cursor: 'pointer',
                          border: `1px solid ${active ? COLORS.gold : COLORS.border}`,
                          background: active
                            ? 'linear-gradient(135deg, rgba(223,169,38,0.16), rgba(223,169,38,0.06))'
                            : 'rgba(17,17,18,0.45)',
                          transition: 'all 0.18s',
                          boxShadow: active ? '0 0 0 1px rgba(223,169,38,0.12), 0 8px 22px rgba(0,0,0,0.24)' : 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = COLORS.surfaceHover; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'rgba(17,17,18,0.45)'; }}
                      >
                        {familySectionImg[f.id] && (
                          <img
                            src={familySectionImg[f.id]}
                            alt=""
                            style={{ width: '100%', maxWidth: 112, height: '4.75rem', objectFit: 'contain', opacity: active ? 1 : 0.72 }}
                          />
                        )}
                        <span style={{
                          fontWeight: 700,
                          color: active ? COLORS.gold : COLORS.text,
                          fontSize: '0.92rem',
                          lineHeight: 1.25
                        }}>
                          {pick(f, 'name')}
                        </span>
                        <span style={{ fontSize: '0.72rem', lineHeight: 1.45, color: COLORS.textMuted, textAlign: 'center' }}>
                          {familySubtitle(f)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>}

              {/* Dimensions */}
              {/* Options */}
              {step === 3 && <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>{t('configurator.options')}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem 0.9rem' }}>
                  {designLoadError && <p role="alert">{designLoadError} <button onClick={() => setDesignReload(n => n + 1)}>{lang === 'et' ? 'Proovi uuesti' : 'Retry'}</button></p>}
                  {availableDesigns.length > 0 && <div>
                    <label style={labelStyle} htmlFor="cfg-studio-design">{lang === 'et' ? 'Ukse disain' : 'Door design'}</label>
                    <select id="cfg-studio-design" style={{ ...inputBaseStyle, ...selectArrow }} value={studioDesign ? studioDesignId : ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        setStudioDesignId(id); setStudioError(''); setAdded(false);
                        setConfig((current: any) => ({ ...current, cottagePattern: undefined }));
                      }}>
                      <option value="">{lang === 'et' ? 'Tavaline' : 'Standard'}</option>
                      {availableDesigns.map((row) => <option key={row.id} value={row.id}>{row.design.name}</option>)}
                    </select>
                    <p style={{ color: COLORS.textMuted, fontSize: '0.75rem', margin: '0.5rem 0 0' }}>{lang === 'et'
                      ? 'Disain määrab aknaavad, nende paigutuse ja pinnamustri: sooned või Cottage’i paneelid. Ukse mõõdud, hinged ja luku valid eraldi.'
                      : 'Designs set window openings, placement and surface patterns: grooves or Cottage panels. Choose door size, hinges and lock separately.'}</p>
                  </div>}
                  {family?.id === 'COTTAGE' && <div>
                    <label style={labelStyle} htmlFor="cfg-cottage-pattern">{panelLabel}</label>
                    <select id="cfg-cottage-pattern" style={{ ...inputBaseStyle, ...selectArrow }} value={cottagePattern}
                      onChange={(e) => setOpt('cottagePattern', e.target.value)}>
                      {COTTAGE_PATTERNS.map(([id, name]) => <option key={id} value={id}>{lang === 'et'
                        ? (id === 'vertical' ? 'Püstised paneelid' : 'Kuusemuster') : name}</option>)}
                    </select>
                  </div>}
                  {OPTION_GROUPS.map(renderSelect)}
                  {/* Waternose — shown as a dropdown for families whose
                      compatibility allows it (40 mm leaf); hidden elsewhere. */}
                  {grouped.extra?.some((o: any) => o.id === 'extra_waternose') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <label style={labelStyle} htmlFor="cfg-waternose">{t('configurator.waternose')}</label>
                      <select
                        id="cfg-waternose"
                        value={extras.includes('extra_waternose') ? 'yes' : 'no'}
                        onChange={(e) => {
                          const on = e.target.value === 'yes';
                          setExtras((xs) => on
                            ? (xs.includes('extra_waternose') ? xs : [...xs, 'extra_waternose'])
                            : xs.filter((x) => x !== 'extra_waternose'));
                          setAdded(false);
                        }}
                        style={{ ...inputBaseStyle, ...selectArrow }}
                      >
                        <option value="yes">{t('configurator.yes')}</option>
                        <option value="no">{t('configurator.no')}</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>}

              {/* Extras */}
              {step === 3 && grouped.extra?.filter((o: any) => o.id !== 'extra_waternose').length > 0 && (
                <div style={cardStyle}>
                  <h2 style={sectionTitleStyle}>{t('configurator.groups.extra')}</h2>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' as const }}>
                    {grouped.extra.filter((o: any) => o.id !== 'extra_waternose').map((o: any) => {
                      const active = extras.includes(o.id);
                      return (
                        <button
                          key={o.id}
                          onClick={() => toggleExtra(o.id)}
                          style={{
                            padding: '0.32rem 0.8rem',
                            borderRadius: '999px',
                            fontSize: '0.78rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            border: `1px solid ${active ? COLORS.goldBorder : COLORS.border}`,
                            background: active ? COLORS.goldSoft : 'rgba(17,17,18,0.4)',
                            color: active ? COLORS.gold : COLORS.textSoft,
                            transition: 'all 0.15s'
                          }}
                        >
                          {pick(o, 'name')}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 2 && <>
              {/* Configuration — door type × sidelights, picked independently */}
              <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>{t('configurator.configuration')}</h2>
                {(() => {
                  const slPart = confType.includes('LEFT') ? 'left'
                    : confType.includes('RIGHT') ? 'right'
                    : confType.includes('TWO') ? 'both' : 'none';
                  const setConf = (dbl: boolean, sl: string) => {
                    const ct = dbl
                      ? (sl === 'left' ? 'DOUBLE_DOOR_LEFT_SIDELIGHT' : sl === 'right' ? 'DOUBLE_DOOR_RIGHT_SIDELIGHT' : sl === 'both' ? 'DOUBLE_DOOR_TWO_SIDELIGHTS' : 'DOUBLE_DOOR')
                      : (sl === 'left' ? 'DOOR_LEFT_SIDELIGHT' : sl === 'right' ? 'DOOR_RIGHT_SIDELIGHT' : sl === 'both' ? 'DOOR_TWO_SIDELIGHTS' : 'SINGLE_DOOR');
                    applyConfType(ct);
                  };
                  const pickBtn = (active: boolean, disabled: boolean): React.CSSProperties => ({
                    flex: 1, padding: '0.5rem 0.3rem', borderRadius: '0.55rem',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: '0.78rem', fontWeight: active ? 700 : 500,
                    opacity: disabled ? 0.35 : 1,
                    border: `1px solid ${active ? COLORS.goldBorder : COLORS.border}`,
                    background: active ? COLORS.goldSoft : 'rgba(17,17,18,0.4)',
                    color: active ? COLORS.gold : COLORS.textSoft,
                    transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.32rem'
                  });
                  const doorGlyph = (dbl: boolean) => (
                    <div style={{ display: 'flex', gap: '2px', height: '1.15rem', alignItems: 'stretch' }}>
                      <div style={{ width: '0.75rem', borderRadius: '2px', background: 'rgba(230,226,220,0.55)' }} />
                      {dbl && <div style={{ width: '0.75rem', borderRadius: '2px', background: 'rgba(230,226,220,0.55)' }} />}
                    </div>
                  );
                  const slGlyph = (sl: string) => (
                    <div style={{ display: 'flex', gap: '2px', height: '1.15rem', alignItems: 'stretch' }}>
                      {(sl === 'left' || sl === 'both') && <div style={{ width: '0.42rem', borderRadius: '2px', background: 'rgba(148,196,236,0.7)' }} />}
                      {isDouble
                        ? <><div style={{ width: '0.5rem', borderRadius: '2px', background: 'rgba(230,226,220,0.5)' }} /><div style={{ width: '0.5rem', borderRadius: '2px', background: 'rgba(230,226,220,0.5)' }} /></>
                        : <div style={{ width: '1.05rem', borderRadius: '2px', background: 'rgba(230,226,220,0.5)' }} />}
                      {(sl === 'right' || sl === 'both') && <div style={{ width: '0.42rem', borderRadius: '2px', background: 'rgba(148,196,236,0.7)' }} />}
                    </div>
                  );
                  return (
                    <>
                      <span style={labelStyle}>{t('configurator.doorType')}</span>
                      <div style={{ display: 'flex', gap: '0.45rem', marginBottom: '0.6rem' }}>
                        <button type="button" onClick={() => setConf(false, slPart)} style={pickBtn(!isDouble, false)}>
                          {doorGlyph(false)}{t('configurator.confTypes.SINGLE_DOOR')}
                        </button>
                        <button type="button" disabled={!doubleAllowed} onClick={() => setConf(true, slPart)} style={pickBtn(isDouble, !doubleAllowed)}>
                          {doorGlyph(true)}{t('configurator.confTypes.DOUBLE_DOOR')}
                        </button>
                      </div>
                      <span style={labelStyle}>{t('configurator.groups.sidelight') || 'Sidelights'}</span>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {(['none', 'left', 'right', 'both'] as const).map((sl) => (
                          <button
                            key={sl}
                            type="button"
                            disabled={sl !== 'none' && !slSupported}
                            onClick={() => setConf(isDouble, sl)}
                            style={pickBtn(slPart === sl, sl !== 'none' && !slSupported)}
                          >
                            {slGlyph(sl)}{t(`configurator.slSides.${sl}`)}
                          </button>
                        ))}
                      </div>
                    </>
                  );
                })()}
                {!slSupported && (
                  <div style={{ color: COLORS.textMuted, fontSize: '0.72rem', marginTop: '0.5rem' }}>
                    {t('configurator.sidelightNotSupported')}
                  </div>
                )}
                {(confType === 'DOOR_TWO_SIDELIGHTS' || confType === 'DOUBLE_DOOR_TWO_SIDELIGHTS') && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginTop: '0.7rem', cursor: 'pointer', color: COLORS.textSoft, fontSize: '0.82rem' }}>
                    <input
                      type="checkbox"
                      checked={slSame}
                      onChange={(e) => { setSlSame(e.target.checked); if (e.target.checked) setSlRight(slLeft); setAdded(false); }}
                    />
                    {t('configurator.sidelightSameBoth')}
                  </label>
                )}
              </div>

              {/* Section layout — one proportional column per element of the
                  set: sidelight | leaf | leaf | sidelight. Widths apply live;
                  sidelight height always follows the door. */}
              {(() => {
                const slMin = Number(catalog?.sidelight?.limits?.min_width_mm) || 200;
                const slMax = Number(catalog?.sidelight?.limits?.max_width_mm) || 1200;
                const glassList = catalog?.sidelight?.glass?.length
                  ? catalog.sidelight.glass
                  : [{ type: 'CLEAR', name: 'Clear glass', nameEt: 'Selge klaas' }, { type: 'FROSTED', name: 'Frosted glass', nameEt: 'Matt klaas' }];
                type Sec = {
                  key: string; label: string; kind: 'leaf' | 'sl';
                  w: number; min: number; max: number; onW: (v: number) => void;
                  slSide?: 'left' | 'right'; slVal?: any; mirrored?: boolean; handleRight?: boolean;
                };
                const sections: Sec[] = [];
                if (hasLeft) {
                  sections.push({
                    key: 'slL', kind: 'sl', label: t('configurator.leftSidelight'),
                    w: slLeft.widthMm, min: slMin, max: slMax,
                    onW: (v) => setSl('left')({ widthMm: v }),
                    slSide: 'left', slVal: slLeft
                  });
                }
                sections.push({
                  key: 'leafL', kind: 'leaf',
                  label: isDouble ? t('configurator.leftLeaf') : t('configurator.doorLeaf'),
                  w: isDouble ? leafW.l : width,
                  min: isDouble ? 300 : family.minWidth,
                  max: isDouble ? 1500 : family.maxWidth,
                  onW: (v) => {
                    if (isDouble) setLeafSide('l', v);
                    else { setWidth(Math.min(family.maxWidth, Math.max(family.minWidth, v))); setAdded(false); }
                  },
                  handleRight: isDouble
                });
                if (isDouble) {
                  sections.push({
                    key: 'leafR', kind: 'leaf', label: t('configurator.rightLeaf'),
                    w: leafW.r, min: 300, max: 1500,
                    onW: (v) => setLeafSide('r', v)
                  });
                }
                if (hasRight) {
                  sections.push({
                    key: 'slR', kind: 'sl', label: t('configurator.rightSidelight'),
                    w: effSlRight.widthMm, min: slMin, max: slMax,
                    onW: (v) => setSl('right')({ widthMm: v }),
                    slSide: 'right', slVal: effSlRight, mirrored: slSame
                  });
                }
                const total = sections.reduce((s, x) => s + x.w, 0) || 1;
                const leafTotal = sections.filter((s) => s.kind === 'leaf').reduce((s, x) => s + x.w, 0);
                return (
                  <div style={cardStyle}>
                    <h2 style={sectionTitleStyle}>{t('configurator.sectionWidths')}</h2>
                    <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'stretch' }}>
                      {sections.map((s) => {
                        const slSet = s.slSide ? setSl(s.slSide) : null;
                        const dividers = s.kind === 'sl'
                          ? (s.slVal?.layout === 'ONE_HORIZONTAL_DIVIDER' ? 1
                            : s.slVal?.layout === 'TWO_HORIZONTAL_DIVIDERS' ? 2
                            : s.slVal?.layout === 'CUSTOM' ? 3 : 0)
                          : 0;
                        return (
                          <div
                            key={s.key}
                            style={{
                              flex: `1 1 ${(s.w / total) * 100}%`, minWidth: '4.8rem',
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: '0.55rem',
                              padding: '0.5rem 0.45rem',
                              background: s.kind === 'sl' ? 'rgba(148,196,236,0.07)' : 'rgba(223,169,38,0.06)',
                              opacity: s.mirrored ? 0.55 : 1,
                              display: 'flex', flexDirection: 'column', gap: '0.35rem'
                            }}
                          >
                            <div style={{
                              position: 'relative', height: '4.4rem', borderRadius: '0.35rem',
                              border: `1px solid ${COLORS.border}`,
                              background: s.kind === 'sl'
                                ? 'linear-gradient(160deg, rgba(148,196,236,0.28), rgba(148,196,236,0.09))'
                                : 'linear-gradient(160deg, rgba(230,226,220,0.17), rgba(230,226,220,0.06))',
                              overflow: 'hidden'
                            }}>
                              {Array.from({ length: dividers }).map((_, i) => (
                                <div key={i} style={{
                                  position: 'absolute', left: 0, right: 0, height: '3px',
                                  background: 'rgba(148,196,236,0.55)',
                                  top: `${((i + 1) / (dividers + 1)) * 100}%`
                                }} />
                              ))}
                              {s.kind === 'leaf' && (
                                <div style={{
                                  position: 'absolute', top: '50%',
                                  [s.handleRight ? 'right' : 'left']: '0.45rem',
                                  width: '0.45rem', height: '0.45rem', borderRadius: '50%',
                                  background: COLORS.gold, transform: 'translateY(-50%)'
                                }} />
                              )}
                            </div>
                            <div style={{
                              fontSize: '0.6rem', color: COLORS.textMuted, textAlign: 'center',
                              textTransform: 'uppercase', letterSpacing: '0.05em',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                            }}>
                              {s.label}{s.mirrored ? ` · ${t('configurator.sidelightSameBoth')}` : ''}
                            </div>
                            <fieldset disabled={s.mirrored} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                              <LiveNumInput
                                value={s.w} min={s.min} max={s.max}
                                onCommit={s.onW}
                                ariaLabel={s.label}
                                style={{ ...inputBaseStyle, padding: '0.32rem 0.2rem', textAlign: 'center', fontSize: '0.78rem' }}
                              />
                              {s.kind === 'sl' && slSet && s.slVal && (
                                <>
                                  <select
                                    value={s.slVal.glassType}
                                    onChange={(e) => slSet({ glassType: e.target.value })}
                                    style={{ ...inputBaseStyle, ...selectArrow, padding: '0.28rem 1.4rem 0.28rem 0.4rem', fontSize: '0.72rem' }}
                                    aria-label={t('configurator.sidelightGlass')}
                                  >
                                    {glassList.map((g: any) => (
                                      <option key={g.type} value={g.type}>{lang === 'et' ? (g.nameEt || g.name) : g.name}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={s.slVal.layout}
                                    onChange={(e) => slSet({ layout: e.target.value })}
                                    style={{ ...inputBaseStyle, ...selectArrow, padding: '0.28rem 1.4rem 0.28rem 0.4rem', fontSize: '0.72rem' }}
                                    aria-label={t('configurator.sidelightLayout')}
                                  >
                                    {SL_LAYOUTS.map((l) => (
                                      <option key={l} value={l}>{t(`configurator.sidelightLayouts.${l}`)}</option>
                                    ))}
                                  </select>
                                </>
                              )}
                            </fieldset>
                          </div>
                        );
                      })}
                    </div>
                    {/* Whole-set height + derived totals */}
                    <div style={{ display: 'flex', gap: '0.7rem', marginTop: '0.6rem', alignItems: 'flex-end' }}>
                      <div style={{ flex: '0 0 8.5rem' }}>
                        <label style={labelStyle} htmlFor="cfg-h">{t('configurator.height')} (mm)</label>
                        <LiveNumInput
                          value={height} min={family.minHeight} max={family.maxHeight}
                          onCommit={(v) => { setHeight(v); setAdded(false); }}
                          ariaLabel={t('configurator.height')}
                          style={inputBaseStyle}
                        />
                        <div style={{ color: COLORS.textMuted, fontSize: '0.68rem', marginTop: '0.25rem' }}>
                          {family.minHeight}–{family.maxHeight} mm
                        </div>
                      </div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.72rem', paddingBottom: '0.5rem' }}>
                        {isDouble
                          ? `${t('configurator.leafWidths')}: ${leafW.l} + ${leafW.r} = ${leafTotal} mm · ${t('configurator.totalWidth')}: ${total} mm`
                          : `${t('configurator.totalWidth')}: ${total} mm`}
                      </div>
                    </div>
                  </div>
                );
              })()}
              </>}

              {/* Handing (opening direction hidden for now) */}
              {step === 2 && <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: '1fr', gap: '1.25rem' }}>
                <div>
                  <span style={labelStyle}>{t('configurator.handing')}</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['left', 'right'].map((sd) => (
                      <button
                        key={sd}
                        onClick={() => { setHanding(sd); setAdded(false); }}
                        style={{
                          flex: 1,
                          padding: '0.55rem 0.3rem',
                          borderRadius: '0.55rem',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          border: `1px solid ${handing === sd ? COLORS.goldBorder : COLORS.border}`,
                          background: handing === sd ? COLORS.goldSoft : 'rgba(17,17,18,0.4)',
                          color: handing === sd ? COLORS.gold : COLORS.textSoft,
                          transition: 'all 0.15s'
                        }}
                      >
                        {t(`configurator.${sd}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'none' }}>
                  <span style={labelStyle}>{t('configurator.opening')}</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['outward', 'inward'].map((d) => {
                      const disabled = family.opening !== 'both' && family.opening !== d;
                      return (
                        <button
                          key={d}
                          disabled={disabled}
                          onClick={() => { setOpening(d); setAdded(false); }}
                          style={{
                            flex: 1,
                            padding: '0.55rem 0.3rem',
                            borderRadius: '0.55rem',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.35 : 1,
                            border: `1px solid ${opening === d ? COLORS.goldBorder : COLORS.border}`,
                            background: opening === d ? COLORS.goldSoft : 'rgba(17,17,18,0.4)',
                            color: opening === d ? COLORS.gold : COLORS.textSoft,
                            transition: 'all 0.15s'
                          }}
                        >
                          {t(`configurator.${d}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>}

              {/* Price card */}
              {step === 4 && <div style={{ ...cardStyle, borderColor: COLORS.goldBorder, background: 'linear-gradient(135deg, rgba(26,26,28,0.9), rgba(20,20,22,0.9))' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem' }}>
                  <span style={labelStyle}>{t('configurator.priceLabel')}</span>
                  <span style={{ ...labelStyle, marginBottom: 0 }}>{t('configurator.qty') || 'Qty'}: {qty}</span>
                </div>
                {!price ? (
                  <p style={{ color: COLORS.textMuted, margin: '0.25rem 0 0', minHeight: '2.4rem', display: 'flex', alignItems: 'center' }}>
                    {t('configurator.priceCalculating')}
                  </p>
                ) : price.retailPrice != null && !price.requiresReview ? (
                  <div>
                    <p style={{ color: COLORS.text, fontSize: '2rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
                      {formatPrice(price.totalPrice ?? price.retailPrice)}
                    </p>
                    {qty > 1 && (
                      <p style={{ color: COLORS.textMuted, fontSize: '0.82rem', margin: '0.2rem 0 0' }}>
                        {formatPrice(price.retailPrice)} / {t('configurator.qty') || 'unit'}
                      </p>
                    )}
                  </div>
                ) : price.retailPrice != null ? (
                  <div>
                    <p style={{ color: COLORS.text, fontSize: '1.15rem', margin: 0, lineHeight: 1.55 }}>
                      {t('configurator.priceEstimate')} {formatPrice(price.totalPrice ?? price.retailPrice)} — {t('configurator.priceReviewNote')}
                    </p>
                  </div>
                ) : (
                  <p style={{ color: COLORS.text, fontSize: '1.05rem', margin: 0 }}>
                    {t('configurator.priceUnconfirmed')}
                  </p>
                )}
                <p style={{ color: COLORS.textMuted, fontSize: '0.76rem', margin: '0.9rem 0 0', lineHeight: 1.5 }}>
                  {t('configurator.priceDisclaimer')}
                </p>
              </div>}

              {/* Add to cart */}
              {step === 4 && <div style={cardStyle}>
                <button
                  onClick={addToCart}
                  disabled={!!studioError}
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.gold,
                    color: '#111',
                    fontWeight: 700,
                    padding: '0.95rem',
                    borderRadius: '0.65rem',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'transform 0.1s, filter 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {t('configurator.addToCart')}
                </button>
                {added && (
                  <div style={{ marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: COLORS.gold, fontSize: '0.9rem', fontWeight: 600 }}>✓ {t('productPage.addedToCart')}</span>
                    <Link to={path('/cart')} style={{ color: COLORS.text, fontWeight: 600, fontSize: '0.85rem', textDecoration: 'underline' }}>
                      {t('productPage.viewCart')}
                    </Link>
                  </div>
                )}
              </div>}

              {/* Summary */}
              {step === 4 && <div style={cardStyle}>
                <h2 style={{ ...sectionTitleStyle, marginBottom: '0.7rem' }}>{t('configurator.summary')}</h2>
                <dl style={{ margin: 0, display: 'grid', gap: '0.4rem' }}>
                  {[
                    [t('configurator.familyLabel'), family && pick(family, 'name')],
                    [t('configurator.dimensions'), `${width} × ${height} mm`],
                    [lang === 'et' ? 'Ukse disain' : 'Door design', studioDesign?.name || (lang === 'et' ? 'Tavaline' : 'Standard')],
                    ...(family.id === 'COTTAGE' ? [[panelLabel, panelName]] : []),
                    [t('configurator.groups.colour'), selectedPublicColour ? publicColourLabel(selectedPublicColour) : config.colour && (optionLabel(config.colour) + (customSystem ? ` · ${colourMatch?.code || (config.colourCode || '').trim() || t('configurator.colourCode.tbc')}` : ''))],
                    ...(hasLeafOpenings ? [[t('configurator.groups.glass'), glassLabel]] : []),
                    ...(glassSurcharge != null ? [[lang === 'et' ? 'Klaasi lisatasu ukse kohta' : 'Glass surcharge per door', glassSurcharge ? `+${formatPrice(glassSurcharge)}` : (lang === 'et' ? 'Hinnas' : 'Included')]] : []),
                    [t('configurator.groups.threshold'), threshold && `${optionLabel(threshold)}${fixedThreshold ? ' · 25 mm' : ''}`],
                    [t('configurator.groups.hinge'), config.hinge && optionLabel(config.hinge)],
                    [t('configurator.configuration'), t(`configurator.confTypes.${confType}`)],
                    ...(isDouble ? [[t('configurator.leafWidths'), `${leafW.l} + ${leafW.r} mm`]] : []),
                    ...(hasLeft ? [[
                      t('configurator.leftSidelight'),
                      `${slLeft.widthMm} × ${slLeft.heightMm ?? height} mm · ${slLeft.glassType} · ${t(`configurator.sidelightLayouts.${slLeft.layout}`)}`
                    ]] : []),
                    ...(hasRight ? [[
                      t('configurator.rightSidelight'),
                      `${effSlRight.widthMm} × ${effSlRight.heightMm ?? height} mm · ${effSlRight.glassType} · ${t(`configurator.sidelightLayouts.${effSlRight.layout}`)}`
                    ]] : []),
                    [t('configurator.handing'), t(`configurator.${handing}`)],
                    [t('cart.qty') || 'Qty', qty],
                    ...(extras.length ? [[t('configurator.groups.extra'), extras.map(optionLabel).join(', ')]] : [])
                  ].map(([k, v]) => (
                    <div key={String(k)} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                      <dt style={{ color: COLORS.textMuted, fontSize: '0.78rem' }}>{k}</dt>
                      <dd style={{ color: COLORS.textSoft, fontSize: '0.82rem', fontWeight: 500, textAlign: 'right' }}>{v || '—'}</dd>
                    </div>
                  ))}
                </dl>
              </div>}

              <div className="cfg-nav-buttons" style={{ display: 'flex', gap: '0.6rem', paddingBottom: '1.5rem' }}>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep((current) => Math.max(1, current - 1))}
                    style={{ ...inputBaseStyle, width: 'auto', flex: 1, cursor: 'pointer', fontWeight: 600 }}
                  >
                    {lang === 'et' ? 'Tagasi' : 'Previous'}
                  </button>
                )}
                {step < WIZARD_STEPS.length && (
                  <button
                    type="button"
                    onClick={() => setStep((current) => Math.min(WIZARD_STEPS.length, current + 1))}
                    style={{
                      ...inputBaseStyle, width: 'auto', flex: 2, cursor: 'pointer', fontWeight: 700,
                      borderColor: COLORS.goldBorder, background: COLORS.gold, color: '#111'
                    }}
                  >
                    {lang === 'et' ? 'Järgmine' : 'Next'}
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN — full-height 3D preview */}
            <div className="cfg-preview-col">
              <div className="cfg-preview-card" style={{ ...cardStyle, padding: 0, borderRadius: 0, border: 'none', borderLeft: `1px solid ${COLORS.border}`, background: 'linear-gradient(180deg, rgba(22,22,24,0.95), rgba(14,14,16,0.98))' }}>
                <div className="cfg-preview-canvas" style={{ background: 'linear-gradient(180deg, rgba(30,30,32,0.8), rgba(20,20,22,0.9))' }}>
                  <span style={{
                    position: 'absolute', top: '0.9rem', right: '1rem', zIndex: 2,
                    fontSize: '0.72rem', color: COLORS.textMuted,
                    textTransform: 'uppercase' as const, letterSpacing: '0.05em',
                    padding: '0.25rem 0.6rem', borderRadius: '999px',
                    background: 'rgba(17,17,18,0.55)', border: `1px solid ${COLORS.border}`,
                    pointerEvents: 'none'
                  }}>
                    {width}×{height} mm
                  </span>
                  <button
                    type="button"
                    className="cfg-ai-btn"
                    onClick={() => { setVizOpen(true); trackEvent('ai_visualization_open', { product: family.id }); }}
                    style={{
                      position: 'absolute', top: '0.9rem', left: '1rem', zIndex: 2,
                      padding: '0.45rem 0.85rem', borderRadius: '999px',
                      border: `1px solid ${COLORS.goldBorder}`, background: 'rgba(17,17,18,0.72)',
                      color: COLORS.gold, fontSize: '0.75rem', fontWeight: 600,
                      letterSpacing: '0.04em', cursor: 'pointer',
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    {t('configurator.aiViz.button')}
                  </button>
                  <ConfiguratorPreview
                    ref={previewRef}
                    previewAngleDeg={openAngle} onPreviewAngleChange={setOpenAngle}
                    previewPassiveAngleDeg={passiveAngle} onPreviewPassiveAngleChange={setPassiveAngle}
                    widthMm={width}
                    heightMm={height}
                    colour={config.colour}
                    customColourHex={colourMatch?.hex ?? null}
                    lighting={visualSettings?.environment || 'daylight'}
                    environmentIntensity={visualSettings?.environmentIntensity ?? 1}
                    environmentAssetPath={visualSettings?.environmentAssetPath}
                    viewportAO={visualSettings?.viewportAO ?? false}
                    aoSettings={visualSettings?.aoSettings}
                    design={doorDesign}
                    glass={leafGlass}
                    leafGeometry={leafGeometry}
                    onSceneError={(_, message) => { if (studioDesign) setStudioError(message); }}
                    onSceneReady={() => setStudioError('')}
                    handing={handing}
                    opening={opening}
                    threshold={threshold}
                    hinges={config.hinge === 'hinge_2' ? 2 : 3}
                    sidelight={config.sidelight}
                    waternose={extras.includes('extra_waternose')}
                    leftSidelight={hasLeft ? { widthMm: slLeft.widthMm, heightMm: slLeft.heightMm ?? height, glassType: slLeft.glassType, layout: slLeft.layout } : null}
                    rightSidelight={hasRight ? { widthMm: effSlRight.widthMm, heightMm: effSlRight.heightMm ?? height, glassType: effSlRight.glassType, layout: effSlRight.layout } : null}
                    leafLeftWidthMm={isDouble ? leafW.l : undefined}
                    leafRightWidthMm={isDouble ? leafW.r : undefined}
                    family={family.id}
                  />
                  {studioError && <p role="alert" style={{ position: 'absolute', bottom: 70, left: 20, right: 20, color: '#ffb4b4', background: '#151b24', padding: 12 }}>{studioError}</p>}
                </div>
                {/* Live price bar — always visible across all wizard steps */}
                <div className="cfg-price-bar" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem',
                  padding: '0.7rem 1rem', borderTop: `1px solid ${COLORS.border}`, textAlign: 'left'
                }}>
                  <div style={{ minWidth: 0 }}>
                    <span className="cfg-price-label" style={labelStyle}>{t('configurator.priceLabel')}</span>
                    {!price ? (
                      <div style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>{t('configurator.priceCalculating')}</div>
                    ) : price.retailPrice != null ? (
                      <div>
                        <span className="cfg-price-value" style={{ color: COLORS.text, fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                          {formatPrice(price.totalPrice ?? price.retailPrice)}
                        </span>
                        {qty > 1 && (
                          <span style={{ color: COLORS.textMuted, fontSize: '0.75rem', marginLeft: '0.4rem' }}>
                            {formatPrice(price.retailPrice)} / {lang === 'et' ? 'tk' : 'pc'}
                          </span>
                        )}
                        {price.requiresReview && (
                          <div className="cfg-price-note" style={{ color: COLORS.textMuted, fontSize: '0.72rem' }}>{t('configurator.priceReviewNote')}</div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: COLORS.textSoft, fontSize: '0.85rem' }}>{t('configurator.priceUnconfirmed')}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                    <label htmlFor="cfg-qty" style={{ ...labelStyle, marginBottom: 0 }}>{t('configurator.qty') || 'Qty'}</label>
                    <input
                      id="cfg-qty" type="number" min={1} max={999} step={1}
                      value={qty}
                      onChange={(e) => { setQty(Math.max(1, Number(e.target.value) || 1)); setAdded(false); }}
                      style={{ ...inputBaseStyle, width: '4rem', padding: '0.3rem 0.4rem', textAlign: 'center' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="cfg-footer"><Footer /></div>
      <FeedbackButton
        className="cfg-feedback-btn"
        getContext={() => ({
          step,
          config: family ? {
            family: family.id, width_mm: width, height_mm: height,
            configuration_type: confType, ...config, extras, handing, opening, qty,
            retail_price: price?.retailPrice ?? null
          } : undefined
        })}
      />
      {vizOpen && (
        <AIDoorVisualizer
          onClose={() => setVizOpen(false)}
          captureDoor={() => previewRef.current?.captureFrontView() ?? Promise.resolve(null)}
        />
      )}
    </div>
  );
};

export default Configurator;
